import { useEffect, useRef, useState } from "react";

/**
 * Open/close state and side effects for the nav drawer, shared between the
 * header's toggle button and the overlay panel (which live in different
 * parts of the DOM, so state is lifted here rather than owned by either).
 *
 * There is deliberately no swipe-from-the-left-edge-to-open gesture: iOS owns
 * that strip for its back-navigation swipe (including in a home-screen web
 * app), and a page can't cancel it, so both fired at once.
 */
const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function useNavDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = "hidden";
    // Move focus to the panel container itself (tabIndex=-1), not the first
    // nav item: a programmatic .focus() on a menu item makes some browsers
    // (notably mobile Safari) paint a :focus-visible ring on it even after a
    // touch tap, which reads as a stray highlight on "Biggest Blowout". From
    // the container, the first Tab still lands on the close button.
    panelRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        close();
        return;
      }
      // Trap Tab inside the open panel — without this, tabbing past the
      // last item escapes into the (visually hidden, off-screen) page
      // content behind it.
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // The toggle/panel are CSS-hidden at >=1024px (see app.css) in favor of
  // NavTabs' inline row, but that's a display:none, not an unmount — if the
  // drawer is left open while the viewport crosses that breakpoint (a
  // resize or rotation), isOpen would stay true with the body scroll lock
  // above still applied and no visible close affordance left on screen.
  // Close it automatically the moment that breakpoint starts matching.
  useEffect(() => {
    const query = window.matchMedia("(min-width: 1024px)");
    function handleChange(e: MediaQueryListEvent) {
      if (e.matches) close();
    }
    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, []);

  function open() {
    setIsOpen(true);
  }

  function close() {
    setIsOpen(false);
    toggleRef.current?.focus();
  }

  return { isOpen, open, close, toggleRef, panelRef };
}
